import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';

// EntityNotes — one polymorphic notes feed for client / property / parcel (public.notes).
// Visibility: parcel note → its property + client (up); property note → every parcel in it (down) + client;
// client note stays at client level. Year is a LABEL/FILTER, never a partition — all years show by default.
// Any signed-in staff may edit a note's text or soft-delete it (deleted_at); each is written to activity_log with who
// did it. The author stays on the note. Notes start collapsed on every visit; expanding is not remembered (Philip 2026-10-29).
//
// Props:
//   level        'client' | 'property' | 'parcel'
//   entityId     string   — client_id | property_id | parcel_id (own level)
//   rollup       { propertyId?, parcelIds?[], propertyIds?[] } — ids to roll in (see rules above)
//   selfLabel    string   — label for own-level notes (e.g. the parcel #, property name, client name)
//   rollupLabels { [`${entity_type}:${entity_id}`]: label } — display labels for rolled-in entities
//   viewingYear  string?  — default year the add box files under (changeable, incl "All years"); pass the current
//                           value year at EVERY level (parcel: valueYear; property/client: the app's current year)
//   staff        { id, name } — the signed-in staff member: author of new notes, and who edits/deletes are logged as
//   years        string[] — year chip options (e.g. ['2021'...'2028'])
//   onCount      (n:number)=>void — total active notes incl rollup (drives the nav badge)
//   isDark       bool
export default function EntityNotes({ level, entityId, rollup = {}, selfLabel = '', rollupLabels = {}, viewingYear, staff, years = [], onCount, isDark = false }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true);
  // filters — year filtering is handled by year-aware search (typing '2026' matches notes filed under 2026); no chip row.
  const [q, setQ] = useState('');
  // ⛔ LEVEL filter, keyed on n.entity_type — NOT the old self-vs-rollup control it replaces.
  // "This property / Rolled-up" INVERTED ITS MEANING depending on the screen you were standing on: on a
  // property, "self" meant property notes; on a parcel, "self" meant parcel notes. So the same control
  // answered a different question per screen, which is what made the filter row confusing rather than useful.
  // entity_type is stable: 'parcel' means parcel wherever you are standing.
  const [levelF, setLevelF] = useState('all');   // 'all' | 'client' | 'property' | 'parcel'
  const [parcelF, setParcelF] = useState('all'); // 'all' | <parcel entity_id> — property/client views only
  const [authorF, setAuthorF] = useState('all');
  // add box
  const [body, setBody] = useState('');
  const [addYear, setAddYear] = useState(viewingYear || 'all'); // 'all' → null
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(''); // surfaced read/write failure (no more silent swallow)

  const rk = JSON.stringify(rollup);
  useEffect(() => { setAddYear(viewingYear || 'all'); }, [viewingYear]);

  async function load() {
    if (!entityId) { setNotes([]); setLoading(false); onCount && onCount(0); return; }
    setLoading(true);
    const ors = [`and(entity_type.eq.${level},entity_id.eq.${entityId})`];
    if (level === 'parcel' && rollup.propertyId) ors.push(`and(entity_type.eq.property,entity_id.eq.${rollup.propertyId})`);
    if (level === 'property' && rollup.parcelIds?.length) ors.push(`and(entity_type.eq.parcel,entity_id.in.(${rollup.parcelIds.join(',')}))`);
    if (level === 'client') {
      if (rollup.propertyIds?.length) ors.push(`and(entity_type.eq.property,entity_id.in.(${rollup.propertyIds.join(',')}))`);
      if (rollup.parcelIds?.length) ors.push(`and(entity_type.eq.parcel,entity_id.in.(${rollup.parcelIds.join(',')}))`);
    }
    const { data, error } = await supabase.from('notes').select('*').is('deleted_at', null).or(ors.join(',')).order('created_at', { ascending: false });
    if (error) { setErr(error.message || 'Could not load notes.'); setNotes([]); setLoading(false); onCount && onCount(0); return; }
    setErr('');
    const rows = data || [];
    setNotes(rows); setLoading(false);
    onCount && onCount(rows.length);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [level, entityId, rk]);

  const toggle = () => setCollapsed(v => !v);
  const isSelf = (n) => n.entity_type === level && n.entity_id === entityId;
  // ⛔ NEVER RENDER A RAW UUID. The fallback used to be `n.entity_id`, so a note on a parcel missing from
  // rollupLabels — a RETIRED parcel, or one filtered out of the caller's list — read as
  // "Parcel note — 8f2a1c3e-7e16-4613-a8a3-8e30aa9334e9". Say what it is and keep enough of the id to find it.
  // One label source for BOTH the note header and the parcel dropdown — two implementations would drift, and
  // an option reading differently from the note it selects is exactly the kind of mismatch that wastes time.
  const entityLabel = (type, id) =>
    rollupLabels[`${type}:${id}`] || `unlisted ${type} (${String(id).slice(0, 8)})`;
  const labelFor = (n) => isSelf(n) ? selfLabel : entityLabel(n.entity_type, n.entity_id);
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const levelsPresent = useMemo(() => [...new Set(notes.map(n => n.entity_type))].sort(), [notes]);
  // Parcels that ACTUALLY HAVE NOTES, derived the same way — a parcel with nothing to show never appears, so
  // the dropdown can never offer an option that yields an empty list. 18 East has 27 parcels; this lists only
  // the ones worth choosing.
  const parcelsWithNotes = useMemo(() => {
    const ids = [...new Set(notes.filter(n => n.entity_type === 'parcel').map(n => n.entity_id))];
    return ids.map(id => ({ id, label: entityLabel('parcel', id) }))
              .sort((a, b) => a.label.localeCompare(b.label));
  }, [notes]);   // eslint-disable-line react-hooks/exhaustive-deps
  // ⛔ RESET A STALE SELECTION. Switch property (or year, or reload) and the chosen parcel may no longer have
  // notes in the loaded set — parcelF would then filter EVERYTHING out and the panel would read as empty for
  // no visible reason. Worse, the <select> would hold a value with no matching <option>, and the browser
  // silently renders option #1 instead — the same trap the addYearOptions comment below warns about, where
  // the control lies about its own state.
  useEffect(() => {
    if (parcelF !== 'all' && !parcelsWithNotes.some(p => p.id === parcelF)) setParcelF('all');
  }, [parcelsWithNotes, parcelF]);

  const authors = useMemo(() => [...new Set(notes.map(n => n.author_name).filter(Boolean))].sort(), [notes]);
  // Add-box year options: the passed years PLUS the current addYear if it's a real year outside that list. Guarantees the
  // <select> always has an option matching addYear — otherwise the browser silently shows option #1 (the "All years on a
  // parcel" bug when viewingYear falls outside a folder_year_config-narrowed valueYears).
  const addYearOptions = useMemo(() => {
    const ys = years.slice();
    if (addYear && addYear !== 'all' && !ys.includes(addYear)) ys.push(addYear);
    return ys.sort((a, b) => Number(a) - Number(b));
  }, [years, addYear]);

  const filtered = useMemo(() => notes.filter(n => {
    if (q) { const ql = q.toLowerCase(); // year-aware: match body OR the value_year label (typing '2026' finds 2026 notes)
      if (!(n.body || '').toLowerCase().includes(ql) && !String(n.value_year || '').includes(q.trim())) return false; }
    if (levelF !== 'all' && n.entity_type !== levelF) return false;
    // ⛔ NARROWS PARCEL NOTES ONLY — property/client notes pass through untouched.
    // Why not "show just this parcel's notes and nothing else": the noise Philip is escaping is 26 OTHER
    // parcels, not the property note, and a property note about a planned project is exactly the context you
    // want while reading one unit's history. Same reasoning that kept the roll-up in the first place.
    // It also means the two filters CANNOT combine into a misleading empty list: picking a parcel while the
    // level filter says "Property notes" still shows the property notes — the parcel choice is simply
    // irrelevant to them — instead of silently emptying the panel and looking broken.
    if (parcelF !== 'all' && n.entity_type === 'parcel' && n.entity_id !== parcelF) return false;
    if (authorF !== 'all' && n.author_name !== authorF) return false;
    return true;
  }), [notes, q, levelF, parcelF, authorF, level, entityId]);

  // property/client: own-level notes flat on top, rolled-up notes grouped by source entity

  async function add() {
    const text = body.trim();
    if (!text || saving) return;
    setSaving(true);
    const { error } = await supabase.from('notes').insert([{ entity_type: level, entity_id: entityId, body: text, value_year: addYear === 'all' ? null : addYear, author_id: staff?.id || null, author_name: staff?.name || null }]);
    setSaving(false);
    if (error) { setErr(error.message || 'Could not save the note.'); return; }
    setErr(''); setBody(''); load();
  }
  // ── EDIT (body only) and DELETE — ANY SIGNED-IN STAFF (Philip, 2026-10-11) ────────────────────────────────────
  // Delete used to be author-only, so a note could only be removed by whoever's id it carried: signed in as Brenna,
  // Philip's imported "To check" notes offered no delete. With only Philip and Brenna in Wigley, and `notes` RLS-off
  // (2026-07-27_phase1_notes_roles_contacts.sql:18; in no RLS batch), the author gate was a UI convention that only
  // got in the way. Both actions are now open to any signed-in staff, and each is LOGGED to activity_log with who did
  // it — an edit with the text it replaced, so a rewrite is never silent. Revisit with the RLS hardening if the
  // team grows.
  //
  // BODY ONLY. value_year, entity_type/entity_id, author_* and created_at are never touched: re-scoping a
  // note's year changes who sees it (a re-scope, not a typo fix), re-attaching it is a move, and the author
  // and original timestamp are the record this edit exists to preserve.
  const logNote = (action, n, details) => supabase.from('activity_log').insert({
    staff_user: staff?.name || 'NTRG Staff', action, entity_type: 'note', entity_id: n.id,
    entity_name: `${cap(n.entity_type)} note${labelFor(n) ? ` — ${labelFor(n)}` : ''}`,
    details: { note_entity_type: n.entity_type, note_entity_id: n.entity_id, author: n.author_name || null, by_staff_id: staff?.id || null, ...details },
  }).then(({ error }) => { if (error) console.warn(`[notes] activity_log (${action}):`, error.message); },
    (e) => console.warn(`[notes] activity_log (${action}):`, e));
  const [editId, setEditId] = useState(null);
  const [editBody, setEditBody] = useState('');
  async function saveEdit(n) {
    const id = n.id;
    const text = editBody.trim();
    if (!text) { setErr('A note cannot be empty. Delete it instead.'); return; }
    // updated_at is stamped so the change is VISIBLE — an unmarked edit is exactly the silent rewrite this
    // feature is meant to avoid. Nothing has ever written it, so every pre-existing note has
    // updated_at == created_at and the "edited" marker is accurate from day one with no backfill.
    const { data, error } = await supabase.from('notes')
      .update({ body: text, updated_at: new Date().toISOString() }).eq('id', id).select('id, body, updated_at');
    if (error || !data?.length) { setErr(`Couldn't save the edit: ${error ? error.message : 'no row updated'}`); return; }
    if (data[0].body !== n.body) logNote('note_edit', n, { previous_body: n.body, body: data[0].body });
    setNotes(prev => prev.map(x => x.id === id ? { ...x, body: data[0].body, updated_at: data[0].updated_at } : x));
    setEditId(null); setEditBody(''); setErr('');
  }

  async function softDelete(n) {
    if (!window.confirm('Delete this note? It stays in history (hidden from view), not erased.')) return;
    const { data, error } = await supabase.from('notes').update({ deleted_at: new Date().toISOString() }).eq('id', n.id).select('id');
    if (error || !data?.length) { setErr(`Could not delete the note: ${error ? error.message : 'no row updated'}`); return; }
    logNote('note_delete', n, { body: n.body });
    setErr(''); load();
  }

  // ── styles (global CSS vars → portable across mounts) ──
  const line = 'var(--line)', slate = 'var(--slate)', ink = isDark ? '#e8eaf2' : 'var(--ink)', muted = isDark ? '#8a92ad' : '#9a978f';
  const inp = { padding: '6px 9px', fontSize: '12px', border: `1px solid ${line}`, borderRadius: '7px', background: isDark ? 'rgba(0,0,0,0.2)' : '#fff', color: ink, outline: 'none' };

  const Note = ({ n }) => (
    <div style={{ padding: '9px 0', borderBottom: `1px solid ${line}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', marginBottom: '3px' }}>
        <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: isSelf(n) ? slate : 'var(--red)' }}>
          {cap(n.entity_type)} note{labelFor(n) ? ` — ${labelFor(n)}` : ''}
        </span>
        <span style={{ fontSize: '10px', color: muted }}>· {n.value_year ? n.value_year : 'all years'}</span>
      </div>
      {editId === n.id ? (
        <div>
          <textarea value={editBody} onChange={e => setEditBody(e.target.value)} rows={3} autoFocus
            style={{ ...inp, width: '100%', fontSize: '13px', lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            <button onClick={() => saveEdit(n)} style={{ ...inp, cursor: 'pointer', fontSize: '11px', fontWeight: 700, color: 'var(--red)', padding: '3px 10px' }}>Save</button>
            <button onClick={() => { setEditId(null); setEditBody(''); }} style={{ ...inp, cursor: 'pointer', fontSize: '11px', padding: '3px 10px' }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: '13px', color: ink, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{n.body}</div>
      )}
      <div style={{ fontSize: '10px', color: muted, marginTop: '3px', display: 'flex', gap: '8px' }}>
        <span>{n.author_name || 'Unknown'} · {new Date(n.created_at).toLocaleDateString()}</span>
        {/* Same date format as the created line — a full date, because whether an edit came minutes or months
            later is the thing worth knowing. Shown only when the row was actually changed: every note predating
            this feature has updated_at == created_at, so the marker never appears retroactively. */}
        {n.updated_at && new Date(n.updated_at) > new Date(n.created_at) &&
          <span>· edited {new Date(n.updated_at).toLocaleDateString()}</span>}
        {/* ANY SIGNED-IN STAFF, edit and delete alike — see EDIT / DELETE above. Both are logged. */}
        {staff?.id && editId !== n.id && <button onClick={() => { setEditId(n.id); setEditBody(n.body); setErr(''); }} style={{ background: 'none', border: 'none', color: slate, cursor: 'pointer', fontSize: '10px', padding: 0, textDecoration: 'underline' }}>edit</button>}
        {staff?.id && <button onClick={() => softDelete(n)} style={{ background: 'none', border: 'none', color: slate, cursor: 'pointer', fontSize: '10px', padding: 0, textDecoration: 'underline' }}>delete</button>}
      </div>
    </div>
  );

  return (
    <div>
      <div onClick={toggle} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
        <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '15px', fontWeight: 700, color: ink }}>Notes ({notes.length})</span>
        <span style={{ color: slate, fontSize: '12px' }}>{collapsed ? '▸' : '▾'}</span>
      </div>

      {!collapsed && (
        <div style={{ marginTop: '10px' }}>
          {err && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', marginBottom: '10px', borderRadius: '7px', border: '1px solid var(--red)', background: 'rgba(180,0,0,0.06)', color: 'var(--red)', fontSize: '12px' }}>
              <span style={{ flex: 1 }}>⚠️ {err}</span>
              <button onClick={() => setErr('')} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline', padding: 0 }}>dismiss</button>
            </div>
          )}
          {/* filters — year filtering is via year-aware search; no chip row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '14px' }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search notes or year (e.g. 2026)…" style={{ ...inp, flex: '1 1 200px', minWidth: '140px' }} />
            {/* Options are the levels actually PRESENT in the loaded notes — a parcel view never offers
                "Client" it cannot show, and the list grows correctly at the client level. */}
            <select value={levelF} onChange={e => setLevelF(e.target.value)} style={inp}>
              <option value="all">All levels</option>
              {levelsPresent.map(t => <option key={t} value={t}>{cap(t)} notes</option>)}
            </select>
            {/* ⛔ PROPERTY AND CLIENT ONLY. A parcel view already shows just its own notes plus its property's,
                so a parcel picker there would filter a list of one parcel — noise, not help. */}
            {level !== 'parcel' && parcelsWithNotes.length > 1 && (
              <select value={parcelF} onChange={e => setParcelF(e.target.value)} style={inp}>
                <option value="all">All parcels</option>
                {parcelsWithNotes.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            )}
            {authors.length > 0 && (
              <select value={authorF} onChange={e => setAuthorF(e.target.value)} style={inp}>
                <option value="all">All authors</option>
                {authors.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
          </div>

          {/* add box */}
          <div style={{ border: `1px solid ${line}`, borderRadius: '9px', padding: '10px', marginBottom: '14px', background: isDark ? 'rgba(255,255,255,0.02)' : '#faf9f7' }}>
            <textarea value={body} onChange={e => setBody(e.target.value)} placeholder={`Add a ${level} note…`} rows={2}
              style={{ width: '100%', boxSizing: 'border-box', ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '7px' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: slate }}>File under:</label>
              <select value={addYear} onChange={e => setAddYear(e.target.value)}
                style={{ ...inp, fontWeight: 700, color: 'var(--red)', borderColor: 'var(--red)', background: isDark ? 'rgba(180,0,0,0.12)' : 'rgba(180,0,0,0.05)' }}>
                {addYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                <option value="all">All years</option>
              </select>
              <button onClick={add} disabled={!body.trim() || saving}
                style={{ marginLeft: 'auto', padding: '6px 16px', fontSize: '12px', fontWeight: 700, border: 'none', borderRadius: '7px', cursor: body.trim() && !saving ? 'pointer' : 'default', background: body.trim() && !saving ? 'var(--red)' : line, color: '#fff' }}>
                {saving ? 'Adding…' : 'Add note'}
              </button>
            </div>
          </div>

          {/* feed — ⛔ ONE DATE-SORTED LIST FOR EVERY LEVEL.
              The parcel branch was already flat; only property/client grouped their rolled-up notes by source
              entity behind a COLLAPSED disclosure. That asymmetry is the whole complaint: standing on a
              property, parcel notes were invisible until expanded, so they appeared to carry no label. They
              always had one — you could not see it. Now the per-note label ("Parcel note — 8940-436-181")
              does the work the group heading did, so 18 East still shows which unit a note belongs to, and
              the newest note reads first whichever level it came from. Both branches rendered the same list
              once flattened, so there is one branch.
              ⛔ The ROLL-UP itself is unchanged and deliberately kept: a property note about a planned
              project matters while you are standing on a parcel valuing it. Use the level filter to narrow. */}
          {loading ? <div style={{ fontSize: '12px', color: muted }}>Loading…</div>
            : filtered.length === 0 ? <div style={{ fontSize: '12px', color: muted }}>No notes{notes.length ? ' match the filters' : ' yet'}.</div>
            : filtered.map(n => <Note key={n.id} n={n} />)}
        </div>
      )}
    </div>
  );
}
